<template>
  <div
    class="trezo-card bg-primary-500 mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[5px] md:mb-[25px] sm:flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0 text-white">{{ directT('ecommerce.orderDetails.trackingID') }}: {{ order?.orderid || 'N/A' }}</h5>
      </div>
      <div v-if="order?.status" class="trezo-card-subtitle mt-[10px] sm:mt-0">
        <h6 class="mb-0 text-white">{{ directT('ecommerce.orderDetails.status') }}: {{ formatStatus(order.status) }}</h6>
      </div>
    </div>

    <div class="trezo-card-content">
      <div class="relative z-[1] sm:flex items-center justify-between">
        <span
          class="block absolute left-0 right-0 top-[25px] -z-[1] border-t border-dashed border-white hidden md:inline-block"
        ></span>
        <div
          class="inline-block md:block ltr:mr-[25px] rtl:ml-[25px] md:ltr:mr-0 md:rtl:ml-0 mt-[15px] md:mt-0"
        >
          <div
            class="rounded-full bg-primary-500 text-center text-white font-semibold w-[50px] h-[50px] leading-[48px] border border-dashed border-white"
            :class="{ 'bg-info-400': isCurrentOrCompleted('order_placed') }"
          >
            01
          </div>
          <span class="block text-white font-semibold mt-[13px]">
            {{ directT('ecommerce.orderDetails.orderPlaced') }}
          </span>
          <span v-if="stepDate('order_placed')" class="block text-white text-xs mt-[5px] opacity-80">
            {{ stepDate('order_placed') }}
          </span>
        </div>
        <div
          class="inline-block md:block ltr:mr-[25px] rtl:ml-[25px] md:ltr:mr-0 md:rtl:ml-0 mt-[15px] md:mt-0"
        >
          <div
            class="rounded-full bg-primary-500 text-center text-white font-semibold w-[50px] h-[50px] leading-[48px] border border-dashed border-white"
            :class="{ 'bg-info-400': isCurrentOrCompleted('packed') }"
          >
            02
          </div>
          <span class="block text-white font-semibold mt-[13px]">
            {{ directT('ecommerce.orderDetails.packed') }}
          </span>
          <span v-if="stepDate('packed')" class="block text-white text-xs mt-[5px] opacity-80">
            {{ stepDate('packed') }}
          </span>
        </div>
        <div
          class="inline-block md:block ltr:mr-[25px] rtl:ml-[25px] md:ltr:mr-0 md:rtl:ml-0 mt-[15px] md:mt-0"
        >
          <div
            class="rounded-full bg-primary-500 text-center text-white font-semibold w-[50px] h-[50px] leading-[48px] border border-dashed border-white"
            :class="{ 'bg-info-400': isCurrentOrCompleted('shipped') }"
          >
            03
          </div>
          <span class="block text-white font-semibold mt-[13px]">
            {{ directT('ecommerce.orderDetails.shipped') }}
          </span>
          <span v-if="stepDate('shipped')" class="block text-white text-xs mt-[5px] opacity-80">
            {{ stepDate('shipped') }}
          </span>
        </div>
        <div
          class="inline-block md:block ltr:mr-[25px] rtl:ml-[25px] md:ltr:mr-0 md:rtl:ml-0 mt-[15px] md:mt-0"
        >
          <div
            class="rounded-full bg-primary-500 text-center text-white font-semibold w-[50px] h-[50px] leading-[48px] border border-dashed border-white ltr:ml-auto rtl:mr-auto"
            :class="{ 'bg-info-400': isCurrentOrCompleted('delivered') }"
          >
            04
          </div>
          <span class="block text-white font-semibold mt-[13px]">
            {{ directT('ecommerce.orderDetails.delivered') }}
          </span>
          <span v-if="stepDate('delivered')" class="block text-white text-xs mt-[5px] opacity-80">
            {{ stepDate('delivered') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from "vue";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import { Order } from "@/services/orderService";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "TrackingID",
  props: {
    order: {
      type: Object as PropType<Order | null>,
      required: true
    }
  },
  setup(props) {
    // i18n setup
    const { locale } = useI18n();
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Format date (short format for status timeline)
    const formatShortDate = (dateString: string): string => {
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        return new Intl.DateTimeFormat(locale.value, {
          month: 'short',
          day: 'numeric'
        }).format(date);
      } catch (e) {
        console.warn('Error formatting date:', e);
        return '';
      }
    };
    
    // Format status for display
    const formatStatus = (status: string): string => {
      // Capitalize first letter of each word
      return status
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };
    
    // Get current status step
    const currentStep = computed(() => {
      if (!props.order || !props.order.status) return 'order_placed';
      
      const status = props.order.status.toLowerCase();
      
      // Map status to steps
      if (status === 'delivered' || status === 'complete' || status === 'completed') return 'delivered';
      if (status === 'shipped' || status === 'in transit' || status === 'shipping') return 'shipped';
      if (status === 'processing' || status === 'packed' || status === 'preparing') return 'packed';
      return 'order_placed';
    });
    
    // Check if a step is current or completed
    const isCurrentOrCompleted = (step: string) => {
      const steps = ['order_placed', 'packed', 'shipped', 'delivered'];
      const currentIndex = steps.indexOf(currentStep.value);
      const stepIndex = steps.indexOf(step);
      
      return stepIndex <= currentIndex;
    };
    
    // Get the date for each step based on status and created/updated dates
    const stepDate = (step: string) => {
      if (!props.order) return '';
      
      const steps = ['order_placed', 'packed', 'shipped', 'delivered'];
      const currentIndex = steps.indexOf(currentStep.value);
      const stepIndex = steps.indexOf(step);
      
      // If the step is not yet reached, don't show a date
      if (stepIndex > currentIndex) return '';
      
      // If the step is the first one (order_placed), use created_at
      if (step === 'order_placed' && props.order.created_at) {
        return formatShortDate(props.order.created_at);
      }
      
      // If the step is the current one, use updated_at
      if (step === currentStep.value && props.order.updated_at) {
        return formatShortDate(props.order.updated_at);
      }
      
      // For completed steps that aren't the first or current, estimate dates
      if (stepIndex < currentIndex && props.order.created_at && props.order.updated_at) {
        // Simple estimation: evenly distribute the time between created and updated date
        try {
          const createdDate = new Date(props.order.created_at).getTime();
          const updatedDate = new Date(props.order.updated_at).getTime();
          const timeSpan = updatedDate - createdDate;
          const stepCount = currentIndex; // Number of steps completed
          
          // Estimate a date for this step based on its position
          const estimatedTime = createdDate + (timeSpan * stepIndex) / stepCount;
          return formatShortDate(new Date(estimatedTime).toISOString());
        } catch (e) {
          console.warn('Error calculating step date:', e);
          return '';
        }
      }
      
      return '';
    };
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    return {
      directT,
      isCurrentOrCompleted,
      currentStep,
      formatStatus,
      stepDate
    };
  }
});
</script>
